import { useForm, UseFormReturnType } from "@mantine/form"
import _ from "lodash"
import { usePostHog } from "posthog-js/react"
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"
import { add, formatQuery } from "react-querybuilder"
import { v4 as uuidv4 } from "uuid"

import { MetricOptionType } from "@costory/types/endpoints/DropdownTypes"
import { AxesResponses } from "@costory/types/endpoints/axes"
import { MetricsResponses } from "@costory/types/endpoints/metrics"
import { SavedViewsResponses } from "@costory/types/endpoints/savedViews"
import {
  defaultSearchEvents,
  filterBarSchemaWhereClauseValidated,
  Filters,
} from "@costory/types/filters"
import {
  AggBy,
  Currency,
  ExplorerChartType,
} from "@costory/types/prisma-client"

import { ALL_OTHERS } from "@costory/shared/const"
import dayjs from "@costory/shared/dayjs"
import { OTHERS, UNLABELLED } from "@costory/shared/utils/explorer_data"
import {
  computePresetDates,
  createSafeParsedFilters,
} from "@costory/shared/utils/filters"
import { checkQueryValid } from "@costory/shared/utils/reactQueryBuilder"

import { SavedViewRedirectPage } from "@costory/front/queries/savedViews"
import {
  DEFAULT_QUERY_BUILDER_FILTER,
  DEFAULT_QUERY_BUILDER_FILTER_RECCURING,
  formatDatasources,
  toReadableDate,
  useFiltersSearchParamsBinding,
} from "@costory/front/utils/filters"

type ChartContextType = {
  axes: AxesResponses.Axis[]
  metrics: MetricsResponses.Metric[]
  defaultHideOneTime?: boolean
  savedView?: SavedViewsResponses.GetSavedViewDetails
  redirectPage: SavedViewRedirectPage
  embed?: boolean
  filterForm?: UseFormReturnType<Filters>
  filters?: Filters
  drillDownInto?: (
    groupBy: string,
    value: string,
    newGroupBy: string,
    mode: "sameWindow" | "newTab",
  ) => void
  setThreshold?: (threshold: number | null) => void
  setExplorerChartType?: (chartType: ExplorerChartType) => void
  resetTo?: (filters: Filters) => void
  selectedMetric?: MetricsResponses.Metric | undefined
  queryError?: boolean
  groupByOptions?: {
    group: string
    items: {
      value: string
      label: string
    }[]
  }[]
  urlFilters?: Filters
  setURLFilters?: (filters: Filters) => void
  referenceFilters?: Filters
  metricsOptions?: MetricOptionType[]
  currentPeriod?: string
  previousPeriod?: string
}

// Do not bother with default context value, see https://www.carlrippon.com/react-context-with-typescript-p4/
export const ChartContext = createContext<ChartContextType>(undefined!)

export const useChartContext = () => {
  const posthog = usePostHog()
  const context = useContext(ChartContext)
  if (!context) {
    throw new Error("useChartContext must be used within a ChartContext")
  }
  const { axes, metrics, defaultHideOneTime, savedView, redirectPage, embed } =
    context

  const [queryError, setQueryError] = useState<boolean>(false)
  const metricsOptions = useMemo(
    () =>
      _.sortBy(metrics)
        .filter((metric) => metric != null && typeof metric === "object")
        .map((metric) => ({
          label: metric.name,
          value: metric.id,
          formula:
            metric && typeof metric === "object" && "formula" in metric
              ? metric.formula
              : undefined,
          technical: metric.technical ?? false,
          group: metric.group ?? MetricsResponses.CostCategory.Cost,
          onlyUSD: metric.onlyUSD ?? false,
          datasource:
            metric &&
            typeof metric === "object" &&
            "technical" in metric &&
            !metric.technical &&
            "datasource" in metric &&
            metric.datasource
              ? { name: metric.datasource.name, id: metric.datasource.id }
              : undefined,
          rawMetric:
            !metric.technical &&
            metric &&
            typeof metric === "object" &&
            "rawMetric" in metric
              ? metric.rawMetric
              : undefined,
          name: metric.name,
          id: metric.id,
        })),
    [metrics],
  )
  const groupByOptions = useMemo(() => formatDatasources(axes), [axes])

  const validateFilters = useCallback((filters: Filters) => {
    const validatedFilters =
      filterBarSchemaWhereClauseValidated.safeParse(filters)
    if (validatedFilters.success) {
      const transformedFilters = {
        ...validatedFilters.data,
        ...(filters.threshold && {
          // @ts-expect-error zod sometimes returns string here
          threshold: parseInt(filters.threshold),
        }),
        whereClause: JSON.parse(
          formatQuery(validatedFilters.data.whereClause, "json_without_ids"),
        ),
        explorerChartType: filters.explorerChartType,
        eventsFilter: filters.eventsFilter ?? defaultSearchEvents,
      }
      return {
        ...validatedFilters,
        data: transformedFilters as Filters,
      }
    }
    return validatedFilters
  }, [])

  const { urlFilters, setURLFilters } =
    useFiltersSearchParamsBinding(validateFilters)

  const defaultFilters = {
    limit: 10,
    aggBy: AggBy.Month,
    metricId: metricsOptions[0]?.value || "cost",
    groupBy: "cos_provider",
    currency: Currency.USD,
    explorerChartType: ExplorerChartType.BAR,
    ...computePresetDates("LAST_MONTH"),
    whereClause: defaultHideOneTime
      ? DEFAULT_QUERY_BUILDER_FILTER_RECCURING
      : DEFAULT_QUERY_BUILDER_FILTER,
    threshold: undefined,
    eventsFilter: defaultSearchEvents,
    bookmarkedEvents: undefined,
  }

  const getFiltersFromSavedViewOrDefault = (
    savedView: SavedViewsResponses.SavedView | undefined,
  ) => {
    if (!savedView) {
      return defaultFilters
    }

    const validatedSavedView = validateFilters(savedView)
    if (!validatedSavedView.success) {
      return defaultFilters
    }

    const bookmarkedEvents = [
      ...savedView.savedViewEvents.map(({ eventId }) => eventId),
      ...savedView.savedViewExternalEvents.map(({ eventId }) => eventId),
    ]

    return {
      ...validatedSavedView.data,
      bookmarkedEvents:
        bookmarkedEvents.length > 0 ? bookmarkedEvents : undefined,
    }
  }

  /**
   * Used to decide if the saved view has been touched
   */
  const referenceFilters = useMemo<Filters>(
    () => getFiltersFromSavedViewOrDefault(savedView),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [savedView],
  )

  /**
   * Used to initialize the form with the saved view, url or default filters
   */
  const initialValues = useMemo<Filters>(
    () => (urlFilters ? urlFilters : referenceFilters),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const [filters, setFilters] = useState<Filters>(initialValues)
  const handleFormValuesChange = (current: Filters) => {
    const { success, data: validatedFilters } = validateFilters(current)
    setQueryError(!success)
    if (!success) {
      return
    }

    setFilters(validatedFilters)
    if (_.isEqual(validatedFilters, referenceFilters)) {
      setURLFilters(null)
    } else {
      setURLFilters(validatedFilters)
    }
  }

  const filterForm = useForm<Filters>({
    initialValues,
    onValuesChange: handleFormValuesChange,
    mode: "uncontrolled",
  })
  /**
   * Change filters on URL change (browser navigation)
   */
  useEffect(() => {
    if (urlFilters && !_.isEqual(urlFilters, filters)) {
      filterForm.setValues({
        ...urlFilters,
        threshold: urlFilters.threshold ?? undefined,
      })
      return
    }

    if (!urlFilters && !_.isEqual(referenceFilters, filters)) {
      resetTo({
        ...referenceFilters,
        threshold: referenceFilters.threshold ?? undefined,
      })
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlFilters])

  const drillDownInto = (
    groupBy: string,
    value: string | undefined,
    newGroupBy: string,
    mode: "sameWindow" | "newTab" = "sameWindow",
    advancedExplorerForm?: UseFormReturnType<Filters>,
  ) => {
    const form = advancedExplorerForm ?? filterForm
    const currentFilters = form.getValues()
    if (value === ALL_OTHERS || value === OTHERS) {
      return
    }
    if (value === "Current" || value === "Previous" || !value) {
      return
    }
    const toSet = { groupBy: newGroupBy }

    // Check if current whereClause is invalid and should be replaced
    const isCurrentQueryValid = checkQueryValid(currentFilters.whereClause, [])
    const baseWhereClause = isCurrentQueryValid
      ? currentFilters.whereClause
      : defaultHideOneTime
        ? DEFAULT_QUERY_BUILDER_FILTER_RECCURING
        : DEFAULT_QUERY_BUILDER_FILTER

    if (value == UNLABELLED) {
      const newWhereClause = add(
        baseWhereClause,
        {
          id: uuidv4(),
          field: groupBy,
          operator: "null",
          value: [],
        },
        [],
      )
      if (mode === "sameWindow") {
        form.setValues({
          whereClause: newWhereClause,
          ...toSet,
        })
      } else {
        const safeParsedFilters = createSafeParsedFilters({
          ...currentFilters,
          whereClause: newWhereClause,
          ...toSet,
        })
        window.open(
          `/explorer?${new URLSearchParams(safeParsedFilters).toString()}`,
          "_blank",
        )
      }
    } else {
      const newRule = {
        id: uuidv4(),
        field: groupBy,
        operator: "in",
        value: [value],
      }

      const newWhereClause =
        baseWhereClause.combinator == "or" && baseWhereClause.rules.length
          ? {
              combinator: "and",
              rules: [
                {
                  combinator: "or",
                  id: uuidv4(),
                  rules: [...baseWhereClause.rules],
                },
                newRule,
              ],
            }
          : add(baseWhereClause, newRule, [])
      if (mode === "sameWindow") {
        form.setValues({
          whereClause: newWhereClause,
          ...toSet,
        })
      } else {
        const safeParsedFilters = createSafeParsedFilters({
          ...currentFilters,
          ...toSet,
          whereClause: newWhereClause,
        })
        window.open(
          `/explorer?${new URLSearchParams(safeParsedFilters).toString()}`,
          "_blank",
        )
      }
    }
  }

  const setThreshold = (threshold: number | null) => {
    filterForm.setFieldValue("threshold", threshold)
  }

  const setExplorerChartType = (chartType: ExplorerChartType) => {
    posthog.capture("Explorer ChartType Changed", {
      chart_type: chartType,
    })
    // Update the form value
    filterForm.setFieldValue("explorerChartType", chartType)
  }

  const resetTo = (filters: Filters) => {
    filterForm.setValues(filters)
  }

  const currentPeriod = useMemo(() => {
    return `${toReadableDate(dayjs(filters.from).toDate())} - ${toReadableDate(dayjs(filters.to).toDate())}`
  }, [filters])
  const previousPeriod = useMemo(() => {
    return `${toReadableDate(dayjs(filters.previousFrom).toDate())} - ${toReadableDate(dayjs(filters.previousTo).toDate())}`
  }, [filters])

  return {
    axes,
    metrics,
    defaultHideOneTime,
    savedView,
    filterForm,
    filters,
    queryError,
    groupByOptions,
    drillDownInto,
    setThreshold,
    setExplorerChartType,
    resetTo,
    selectedMetric: metrics.find((m) => m.id === filters.metricId),
    redirectPage,
    embed,
    referenceFilters,
    urlFilters,
    setURLFilters,
    metricsOptions,
    currentPeriod,
    previousPeriod,
  }
}
